import { clearInitialItemRoute } from './initial-item-route.js';

const ROUTE_TAB_TO_VIEW = {
  restaurants: 'restaurantsView',
  recipes: 'recipesView',
  infos: 'infosView'
};

const ROUTE_VIEW_TO_TAB = {
  restaurantsView: 'restaurants',
  recipesView: 'recipes',
  infosView: 'infos'
};

const ROUTE_TAB_TO_PATH = {
  restaurants: 'restaurants',
  recipes: 'recipes',
  infos: 'infos'
};

const ROUTE_ITEM_TYPE_TO_TAB = {
  restaurant: 'restaurants',
  recipe: 'recipes',
  info: 'infos'
};

const ROUTE_ITEM_TYPE_TO_PARAM = {
  restaurant: 'restaurant',
  recipe: 'recipe',
  info: 'info'
};

const ROUTE_ITEM_TYPE_TO_PATH = {
  restaurant: 'restaurants',
  recipe: 'recipes',
  info: 'infos'
};

const ROUTE_PATH_TO_ITEM_TYPE = {
  restaurants: 'restaurant',
  recipes: 'recipe',
  infos: 'info'
};

const ROUTE_KEYS = ['tab', 'restaurant', 'recipe', 'info'];

function normalizeRouteTab(tab) {
  return ROUTE_TAB_TO_VIEW[tab] ? tab : 'restaurants';
}

function normalizeRouteItem(item) {
  if (!item || typeof item !== 'object') {
    return null;
  }

  const type = typeof item.type === 'string' ? item.type : '';
  const id = typeof item.id === 'string' ? item.id.trim() : '';
  if (!ROUTE_ITEM_TYPE_TO_PARAM[type] || !id) {
    return null;
  }

  return {
    type,
    id
  };
}

function normalizeRouteState(state) {
  const tab = normalizeRouteTab(state?.tab);
  const item = normalizeRouteItem(state?.item);
  if (!item) {
    return {
      tab,
      item: null,
      home: state?.home === true && tab === 'restaurants'
    };
  }

  const routeTabForItem = ROUTE_ITEM_TYPE_TO_TAB[item.type];
  if (!routeTabForItem) {
    return { tab, item: null, home: false };
  }

  if (routeTabForItem !== tab) {
    return { tab: routeTabForItem, item, home: false };
  }

  return { tab, item, home: false };
}

function isSameRouteState(left, right) {
  const leftItem = left?.item;
  const rightItem = right?.item;

  if (left?.tab !== right?.tab) {
    return false;
  }

  if (!leftItem && !rightItem) {
    return left?.home === right?.home;
  }

  if (!leftItem || !rightItem) {
    return false;
  }

  return leftItem.type === rightItem.type && leftItem.id === rightItem.id;
}

function parseRouteStateFromLocation() {
  const params = new URLSearchParams(window.location.search);
  const explicitTab = params.get('tab');
  const sectionMatch = window.location.pathname.match(/^\/(restaurants|recipes|infos)\/?$/);
  let item = null;

  const restaurantId = params.get('restaurant');
  const recipeId = params.get('recipe');
  const infoId = params.get('info');

  if (restaurantId) {
    item = { type: 'restaurant', id: restaurantId };
  } else if (recipeId) {
    item = { type: 'recipe', id: recipeId };
  } else if (infoId) {
    item = { type: 'info', id: infoId };
  }

  if (!item) {
    const pathMatch = window.location.pathname.match(/^\/(restaurants|recipes|infos)\/([^/]+)\/?$/);
    const pathItemType = pathMatch ? ROUTE_PATH_TO_ITEM_TYPE[pathMatch[1]] : '';
    if (pathItemType) {
      try {
        item = {
          type: pathItemType,
          id: decodeURIComponent(pathMatch[2])
        };
      } catch {
        item = null;
      }
    }
  }

  const inferredTab = item
    ? ROUTE_ITEM_TYPE_TO_TAB[item.type]
    : sectionMatch?.[1] || 'restaurants';
  return normalizeRouteState({
    tab: explicitTab || inferredTab,
    item,
    home: !item && !explicitTab && window.location.pathname === '/'
  });
}

function buildRouteUrl(nextRouteState) {
  const normalized = normalizeRouteState(nextRouteState);
  const url = new URL(window.location.href);

  ROUTE_KEYS.forEach((key) => url.searchParams.delete(key));

  if (normalized.item) {
    const pathName = ROUTE_ITEM_TYPE_TO_PATH[normalized.item.type];
    if (pathName) {
      url.pathname = `/${pathName}/${encodeURIComponent(normalized.item.id)}/`;
    }
  } else if (normalized.home) {
    url.pathname = '/';
  } else {
    url.pathname = `/${ROUTE_TAB_TO_PATH[normalized.tab]}/`;
  }

  const query = url.searchParams.toString();
  return `${url.pathname}${query ? `?${query}` : ''}${url.hash}`;
}

function findTileByRouteId(target, routeId) {
  if (!target || !routeId) {
    return null;
  }

  const tiles = target.querySelectorAll('.tile[data-route-id]');
  for (const tile of tiles) {
    if (tile.dataset.routeId === routeId) {
      return tile;
    }
  }

  return null;
}

export function initRouter({
  switchView,
  tileExpander,
  infoReader,
  restaurantList,
  recipeList,
  getInfoItemsById,
  onTitleUpdate = null
}) {
  if (
    typeof switchView !== 'function' ||
    !tileExpander ||
    !infoReader ||
    !restaurantList ||
    !recipeList
  ) {
    return {
      updateRouteFromUI: () => {},
      applyRouteFromLocation: () => {},
      handlePopState: () => {},
      markRestaurantsLoaded: () => {},
      markRecipesLoaded: () => {},
      handleTileClose: () => {},
      handleInfoClose: () => {}
    };
  }

  let routeState = {
    tab: 'restaurants',
    item: null,
    home: true
  };
  let pendingRouteItem = null;
  let restaurantsLoaded = false;
  let recipesLoaded = false;
  let suppressTileCloseRouteSync = false;
  let suppressInfoCloseRouteSync = false;

  function readTileTitle(target, routeId) {
    return findTileByRouteId(target, routeId)?.querySelector('.tile-title')?.textContent?.trim() || '';
  }

  function resolveRouteItemTitle(nextRouteState) {
    const item = nextRouteState?.item;
    if (!item) {
      return '';
    }

    if (item.type === 'restaurant') {
      return readTileTitle(restaurantList, item.id);
    }

    if (item.type === 'recipe') {
      return readTileTitle(recipeList, item.id);
    }

    if (item.type === 'info') {
      const infoItemsById = typeof getInfoItemsById === 'function' ? getInfoItemsById() : null;
      return infoItemsById instanceof Map ? infoItemsById.get(item.id)?.title || '' : '';
    }

    return '';
  }

  function updateDocumentTitle(nextRouteState = routeState, itemTitle = resolveRouteItemTitle(nextRouteState)) {
    if (typeof onTitleUpdate === 'function') {
      onTitleUpdate(nextRouteState, itemTitle);
    }
  }

  function writeRouteToHistory(nextRouteState, { replace = false } = {}) {
    const nextUrl = buildRouteUrl(nextRouteState);
    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (nextUrl === currentUrl) {
      return;
    }

    const method = replace ? 'replaceState' : 'pushState';
    window.history[method](nextRouteState, '', nextUrl);
  }

  function setRouteState(nextRouteState, { replace = false } = {}) {
    const normalized = normalizeRouteState(nextRouteState);
    const changed = !isSameRouteState(routeState, normalized);

    routeState = normalized;
    pendingRouteItem = normalized.item ? { ...normalized.item } : null;
    if (!normalized.item) {
      clearInitialItemRoute(document.querySelector('.view.active'));
    }

    if (changed || replace) {
      writeRouteToHistory(normalized, { replace });
    }

    return normalized;
  }

  function updateRouteFromUI(patch, { replace = false } = {}) {
    const hasItemPatch = Object.prototype.hasOwnProperty.call(patch, 'item');
    const nextRouteState = normalizeRouteState({
      tab: typeof patch.tab === 'string' ? patch.tab : routeState.tab,
      item: hasItemPatch ? patch.item : routeState.item,
      home: false
    });

    setRouteState(nextRouteState, { replace });
    updateDocumentTitle(normalizeRouteState(nextRouteState));
  }

  function openPendingTile(type, id) {
    const target = type === 'restaurant' ? restaurantList : recipeList;
    const isLoaded = type === 'restaurant' ? restaurantsLoaded : recipesLoaded;
    if (!isLoaded) {
      return;
    }

    const openTile = () => {
      if (!pendingRouteItem || pendingRouteItem.type !== type || pendingRouteItem.id !== id) {
        return;
      }

      const tileElement = findTileByRouteId(target, id);
      if (!tileElement) {
        updateRouteFromUI({ item: null }, { replace: true });
        return;
      }

      tileExpander.open(tileElement);
      updateDocumentTitle(routeState, readTileTitle(target, id));
      pendingRouteItem = null;
    };

    if (!tileExpander.isOpen()) {
      openTile();
      return;
    }

    if (tileExpander.getActiveRouteId() === id) {
      pendingRouteItem = null;
      return;
    }

    suppressTileCloseRouteSync = true;
    tileExpander.close();
    window.setTimeout(openTile, 430);
  }

  function tryOpenPendingRouteItem() {
    if (!pendingRouteItem) {
      return;
    }

    const { type, id } = pendingRouteItem;
    if (type === 'info') {
      const infoItemsById = typeof getInfoItemsById === 'function' ? getInfoItemsById() : null;
      if (!(infoItemsById instanceof Map)) {
        return;
      }

      const infoItem = infoItemsById.get(id);
      if (!infoItem) {
        updateRouteFromUI({ item: null }, { replace: true });
        return;
      }

      infoReader.open(infoItem);
      updateDocumentTitle(routeState, infoItem.title || '');
      pendingRouteItem = null;
      return;
    }

    if (type === 'restaurant' || type === 'recipe') {
      openPendingTile(type, id);
      return;
    }

    updateRouteFromUI({ item: null }, { replace: true });
  }

  function applyRouteState(nextRouteState, { replace = false } = {}) {
    const normalized = setRouteState(nextRouteState, { replace });
    switchView(ROUTE_TAB_TO_VIEW[normalized.tab]);
    updateDocumentTitle(normalized);

    const wantsInfoOpen = normalized.item?.type === 'info';
    const wantsTileOpen =
      normalized.item?.type === 'restaurant' || normalized.item?.type === 'recipe';

    if (!wantsInfoOpen && infoReader.isOpen()) {
      suppressInfoCloseRouteSync = true;
      infoReader.close();
    }

    if (!wantsTileOpen && tileExpander.isOpen()) {
      suppressTileCloseRouteSync = true;
      tileExpander.close();
    }

    tryOpenPendingRouteItem();
  }

  return {
    updateRouteFromUI,
    applyRouteFromLocation({ replace = false } = {}) {
      applyRouteState(parseRouteStateFromLocation(), { replace });
    },
    handlePopState() {
      applyRouteState(parseRouteStateFromLocation(), { replace: true });
    },
    markRestaurantsLoaded() {
      restaurantsLoaded = true;
      tryOpenPendingRouteItem();
    },
    markRecipesLoaded() {
      recipesLoaded = true;
      tryOpenPendingRouteItem();
    },
    handleTileClose() {
      if (suppressTileCloseRouteSync) {
        suppressTileCloseRouteSync = false;
        return;
      }

      updateRouteFromUI({ item: null });
    },
    handleInfoClose() {
      if (suppressInfoCloseRouteSync) {
        suppressInfoCloseRouteSync = false;
        return;
      }

      updateRouteFromUI({ item: null });
    }
  };
}

export { ROUTE_VIEW_TO_TAB };
